
import React,{useCallback,useEffect,useMemo,useState}from"react";
import{
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Paper,
  Stack,
  Typography
}from"@mui/material";
import{useDashboardTheme}from"../../../../context/DashboardThemeContext";
import{useAlert}from"../../../../context/AlertContext";
import{useAuth}from"../../../../context/AuthContext";

const API_BASE="/api/access-control";

function isPrivileged(user){
  return user?.role==="super_admin"||user?.role==="admin";
}

export default function PermissionsSettings(){
  const{dashboardTheme}=useDashboardTheme();
  const{showSuccess,showError,showInfo}=useAlert();
  const{accessToken}=useAuth();

  const[users,setUsers]=useState([]);
  const[permissions,setPermissions]=useState([]);
  const[selectedUserId,setSelectedUserId]=useState(null);
  const[selectedPermissions,setSelectedPermissions]=useState([]);
  const[loading,setLoading]=useState(true);
  const[saving,setSaving]=useState(false);
  const[dirty,setDirty]=useState(false);

  const selectedUser=useMemo(
    ()=>users.find(item=>item.id===selectedUserId)||null,
    [users,selectedUserId]
  );

  const privileged=isPrivileged(selectedUser);

  const groupedPermissions=useMemo(()=>{
    return permissions.reduce((result,permission)=>{
      const key=permission.module_label||permission.module;

      if(!result[key]){
        result[key]=[];
      }

      result[key].push(permission);
      return result;
    },{});
  },[permissions]);

  const allActiveCodes=useMemo(
    ()=>permissions.map(item=>item.code),
    [permissions]
  );

  const effectivePermissions=privileged
    ?allActiveCodes
    :selectedPermissions;

  const inputHeaders=useCallback(()=>({
    Authorization:`Bearer ${accessToken}`,
    "Content-Type":"application/json"
  }),[accessToken]);

  const loadData=useCallback(async()=>{
    if(!accessToken){
      setLoading(false);
      showError("No se encontró una sesión activa.");
      return;
    }

    setLoading(true);

    try{
      const[usersResponse,permissionsResponse]=await Promise.all([
        fetch(`${API_BASE}/users/`,{
          headers:inputHeaders()
        }),
        fetch(`${API_BASE}/permissions/`,{
          headers:inputHeaders()
        })
      ]);

      const usersData=await usersResponse.json().catch(()=>({}));
      const permissionsData=await permissionsResponse.json().catch(()=>({}));

      if(!usersResponse.ok){
        throw new Error(
          usersData.detail||
          usersData.error||
          "No se pudieron cargar los usuarios."
        );
      }

      if(!permissionsResponse.ok){
        throw new Error(
          permissionsData.detail||
          permissionsData.error||
          "No se pudieron cargar los permisos."
        );
      }

      const loadedUsers=Array.isArray(usersData.results)
        ?usersData.results
        :[];

      const loadedPermissions=Array.isArray(permissionsData.results)
        ?permissionsData.results
        :[];

      setUsers(loadedUsers);
      setPermissions(loadedPermissions);

      if(loadedUsers.length){
        const firstUser=loadedUsers[0];

        setSelectedUserId(firstUser.id);
        setSelectedPermissions(
          Array.isArray(firstUser.permissions)
            ?firstUser.permissions
            :[]
        );
      }else{
        setSelectedUserId(null);
        setSelectedPermissions([]);
        showInfo("No hay usuarios disponibles.");
      }

      setDirty(false);
    }catch(error){
      showError(error.message||"Error al cargar permisos.");
    }finally{
      setLoading(false);
    }
  },[accessToken,inputHeaders,showError,showInfo]);

  useEffect(()=>{
    loadData();
  },[loadData]);

  const handleSelectUser=user=>{
    if(saving)return;

    setSelectedUserId(user.id);
    setSelectedPermissions(
      Array.isArray(user.permissions)
        ?user.permissions
        :[]
    );
    setDirty(false);
  };

  const handleTogglePermission=code=>{
    if(privileged||saving)return;

    setSelectedPermissions(previous=>{
      if(previous.includes(code)){
        return previous.filter(item=>item!==code);
      }

      return [...previous,code];
    });

    setDirty(true);
  };

  const handleSelectAll=()=>{
    if(privileged||saving)return;

    setSelectedPermissions(allActiveCodes);
    setDirty(true);
  };

  const handleClearAll=()=>{
    if(privileged||saving)return;

    setSelectedPermissions([]);
    setDirty(true);
  };

  const handleSave=async()=>{
    if(!selectedUser){
      showInfo("Selecciona un usuario.");
      return;
    }

    if(privileged){
      showInfo("Los administradores tienen acceso completo por su rol.");
      return;
    }

    if(!accessToken){
      showError("No se encontró una sesión activa.");
      return;
    }

    if(!dirty||saving)return;

    setSaving(true);

    try{
      const response=await fetch(
        `${API_BASE}/users/${selectedUser.id}/permissions/`,
        {
          method:"PUT",
          headers:inputHeaders(),
          body:JSON.stringify({
            permissions:selectedPermissions
          })
        }
      );

      const data=await response.json().catch(()=>({}));

      if(!response.ok){
        throw new Error(
          data.detail||
          data.error||
          "No se pudieron guardar los permisos."
        );
      }

      const savedPermissions=Array.isArray(data.permissions)
        ?data.permissions
        :selectedPermissions;

      setUsers(previous=>previous.map(item=>(
        item.id===selectedUser.id
          ?{...item,permissions:savedPermissions}
          :item
      )));

      setSelectedPermissions(savedPermissions);
      setDirty(false);
      showSuccess("Permisos actualizados correctamente.");
    }catch(error){
      showError(error.message||"Error al guardar permisos.");
    }finally{
      setSaving(false);
    }
  };

  const borderColor=dashboardTheme.border_color;
  const cardColor=dashboardTheme.card_background;
  const backgroundColor=dashboardTheme.dashboard_background;
  const textColor=dashboardTheme.text_color;
  const mutedColor=dashboardTheme.muted_text_color;
  const primaryColor=dashboardTheme.primary_color;
  const buttonColor=dashboardTheme.button_color;
  const buttonTextColor=dashboardTheme.button_text_color;

  return(
    <Paper
      elevation={0}
      sx={{
        borderRadius:"16px",
        border:`1px solid ${borderColor}`,
        bgcolor:cardColor,
        p:{xs:2,md:3},
        boxShadow:"none"
      }}
    >
      <Stack spacing={2.5}>
        <Box>
          <Typography
            sx={{
              fontSize:{xs:22,md:26},
              fontWeight:700,
              color:textColor,
              lineHeight:1.2
            }}
          >
            Control de accesos
          </Typography>

          <Typography
            sx={{
              mt:0.7,
              fontSize:13,
              fontWeight:400,
              color:mutedColor,
              lineHeight:1.6
            }}
          >
            Administra los permisos del personal. Los administradores tienen acceso completo automáticamente.
          </Typography>
        </Box>

        {loading?(
          <Box
            sx={{
              py:6,
              display:"flex",
              justifyContent:"center"
            }}
          >
            <CircularProgress
              size={27}
              sx={{color:primaryColor}}
            />
          </Box>
        ):(
          <Stack
            direction={{xs:"column",md:"row"}}
            spacing={2}
          >
            <Paper
              elevation={0}
              sx={{
                width:{xs:"100%",md:290},
                flexShrink:0,
                borderRadius:"12px",
                border:`1px solid ${borderColor}`,
                bgcolor:backgroundColor,
                overflow:"hidden",
                boxShadow:"none",
                alignSelf:"flex-start"
              }}
            >
              <Box
                sx={{
                  p:1.8,
                  borderBottom:`1px solid ${borderColor}`
                }}
              >
                <Typography
                  sx={{
                    fontSize:14,
                    fontWeight:600,
                    color:textColor
                  }}
                >
                  Usuarios
                </Typography>
              </Box>

              <Box
                sx={{
                  maxHeight:550,
                  overflowY:"auto"
                }}
              >
                {users.map(item=>{
                  const active=item.id===selectedUserId;

                  return(
                    <Box
                      key={item.id}
                      onClick={()=>handleSelectUser(item)}
                      sx={{
                        px:1.8,
                        py:1.5,
                        cursor:saving?"default":"pointer",
                        borderBottom:`1px solid ${borderColor}`,
                        bgcolor:active
                          ?`${primaryColor}18`
                          :"transparent",
                        borderLeft:active
                          ?`3px solid ${primaryColor}`
                          :"3px solid transparent",
                        "&:hover":{
                          bgcolor:`${primaryColor}12`
                        }
                      }}
                    >
                      <Typography
                        noWrap
                        sx={{
                          fontSize:13,
                          fontWeight:active?700:600,
                          color:textColor
                        }}
                      >
                        {item.name}
                      </Typography>

                      <Typography
                        noWrap
                        sx={{
                          mt:0.3,
                          fontSize:11.5,
                          color:mutedColor
                        }}
                      >
                        {item.email}
                      </Typography>

                      <Typography
                        sx={{
                          mt:0.5,
                          fontSize:11,
                          color:mutedColor
                        }}
                      >
                        {item.role==="super_admin"
                          ?"Superadministrador"
                          :item.role==="admin"
                            ?"Administrador"
                            :"Personal"}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            </Paper>

            <Box
              sx={{
                flex:1,
                minWidth:0
              }}
            >
              {!selectedUser?(
                <Typography
                  sx={{
                    fontSize:13,
                    color:mutedColor
                  }}
                >
                  Selecciona un usuario.
                </Typography>
              ):(
                <Stack spacing={2}>
                  <Box>
                    <Typography
                      sx={{
                        fontSize:17,
                        fontWeight:600,
                        color:textColor
                      }}
                    >
                      {selectedUser.name}
                    </Typography>

                    <Typography
                      sx={{
                        fontSize:12.5,
                        color:mutedColor,
                        mt:0.4
                      }}
                    >
                      {selectedUser.email}
                    </Typography>
                  </Box>

                  {privileged?(
                    <Alert
                      severity="info"
                      sx={{
                        borderRadius:"10px",
                        fontSize:12.5
                      }}
                    >
                      Este usuario tiene todos los permisos por su rol de administrador. No necesita asignaciones individuales.
                    </Alert>
                  ):(
                    <Box
                      sx={{
                        display:"flex",
                        gap:1,
                        flexWrap:"wrap",
                        alignItems:"center",
                        justifyContent:"flex-end"
                      }}
                    >
                      <Button
                        size="small"
                        disabled={saving}
                        onClick={handleSelectAll}
                        sx={{
                          textTransform:"none",
                          fontWeight:600,
                          fontSize:12,
                          color:primaryColor
                        }}
                      >
                        Seleccionar todos
                      </Button>

                      <Button
                        size="small"
                        disabled={saving}
                        onClick={handleClearAll}
                        sx={{
                          textTransform:"none",
                          fontWeight:600,
                          fontSize:12,
                          color:mutedColor
                        }}
                      >
                        Quitar todos
                      </Button>
                    </Box>
                  )}

                  {Object.entries(groupedPermissions).map(([group,items])=>(
                    <Paper
                      key={group}
                      elevation={0}
                      sx={{
                        p:1.8,
                        borderRadius:"12px",
                        border:`1px solid ${borderColor}`,
                        bgcolor:backgroundColor,
                        boxShadow:"none"
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize:14,
                          fontWeight:600,
                          mb:1.4,
                          color:textColor
                        }}
                      >
                        {group}
                      </Typography>

                      <Box
                        sx={{
                          display:"grid",
                          gridTemplateColumns:{
                            xs:"1fr",
                            sm:"repeat(2,minmax(0,1fr))"
                          },
                          columnGap:1.5,
                          rowGap:0.3
                        }}
                      >
                        {items.map(permission=>(
                          <FormControlLabel
                            key={permission.code}
                            control={
                              <Checkbox
                                size="small"
                                checked={effectivePermissions.includes(permission.code)}
                                disabled={privileged||saving}
                                onChange={()=>handleTogglePermission(permission.code)}
                                sx={{
                                  color:mutedColor,
                                  "&.Mui-checked":{
                                    color:primaryColor
                                  },
                                  "&.Mui-disabled.Mui-checked":{
                                    color:primaryColor,
                                    opacity:0.75
                                  }
                                }}
                              />
                            }
                            label={permission.name}
                            sx={{
                              m:0,
                              minWidth:0,
                              color:textColor,
                              "& .MuiFormControlLabel-label":{
                                fontSize:12.5,
                                fontWeight:400
                              },
                              "& .MuiFormControlLabel-label.Mui-disabled":{
                                color:textColor,
                                opacity:0.8
                              }
                            }}
                          />
                        ))}
                      </Box>
                    </Paper>
                  ))}

                  {!privileged&&(
                    <Box
                      sx={{
                        display:"flex",
                        justifyContent:"flex-end",
                        pt:0.5
                      }}
                    >
                      <Button
                        disabled={saving||!dirty}
                        onClick={handleSave}
                        variant="contained"
                        disableElevation
                        sx={{
                          minHeight:38,
                          px:2.5,
                          borderRadius:"10px",
                          textTransform:"none",
                          fontSize:13,
                          fontWeight:600,
                          bgcolor:buttonColor,
                          color:buttonTextColor,
                          boxShadow:"none",
                          "&:hover":{
                            bgcolor:buttonColor,
                            boxShadow:"none"
                          }
                        }}
                      >
                        {saving?"Guardando...":"Guardar permisos"}
                      </Button>
                    </Box>
                  )}
                </Stack>
              )}
            </Box>
          </Stack>
        )}
      </Stack>
    </Paper>
  );
}
